import Decimal from "decimal.js";
import Enumerable from "linq";
import { TextLengthCalculator } from "./TextLengthCalculator";

class SplitUnit {
  constructor(public character: string, public isSplitCharacter: boolean, public length: number) {
  }
}

/**
 * 禁則処理の試作
 * 現状は未使用
 */
export class TextSplitter {
  constructor(private _isWordBreakControlled: boolean) {
  }
  
  /**
   * 禁則処理も考慮して行を分割する
   */
  public split(text: string, maxLength: number): string[] {
    if (!text) {
      return [text];
    }
    const splitted: string[] = [];
    const lines = text.split("\n");
    for (const line of lines) {
      const lineTexts = this.splitLine(this.removeLastNewLine(line), maxLength);
      for (const lineText of lineTexts) {
        splitted.push(lineText);
      }
    }
    return splitted;
  }
  
  private removeLastNewLine(text: string): string {
    return text.replace(/\n$/g, "").replace(/\r$/g, "");
  }
  
  private splitLine(text: string, maxLength: number): string[] {
    if (TextLengthCalculator.calculateLength(text) <= maxLength) {
      // 最大長さ内に行が収まっている場合は1行となる
      return [text];
    } 

    // 最大長さ内に行が収まっていない場合は行を分割する
    const splitted: string[] = [];
    const chars = text.split("");
    let start = 0;
    while (true) {
      const units: SplitUnit[] = [];
      for (let current = start; current < chars.length; current++) {
        const currentChar = chars[current];
        const length = TextLengthCalculator.calculateLength(currentChar);
        const isSplitCharacter = this.isSplitCharacter(start, current, chars.length, chars);
        const unit = new SplitUnit(chars[current], isSplitCharacter, length);
        units.push(unit);

        // 浮動小数点の足し算でも誤差が出るので、Decimal経由で計算している
        const currentUnitsLength = Enumerable.from(units)
          .select(u => u.length)  
          .aggregate((p, c) => new Decimal(p).add(c).toNumber());

        const nextChar = (current + 1 < chars.length) ? chars[current + 1] : "";
        const nextCharLength = TextLengthCalculator.calculateLength(nextChar);

        if (new Decimal(currentUnitsLength).add(nextCharLength).toNumber() > maxLength || !nextChar) {
          // 行の終わりは禁則処理不要のはず
          const breakedUnits = !nextChar ? units : this.breakUnits(units);
          const breakedText = this.unitsToString(breakedUnits);
          splitted.push(breakedText);
          start += breakedText.length;
          break;
        }
      }
      if (start >= chars.length) {
        break;
      }
    }
    return splitted;
  }

  private unitsToString(units: SplitUnit[]): string {
    return Enumerable.from(units)
      .select(u => u.character)
      .toArray()
      .join("");
  }

  /**
   * 後ろから見ていって最初に折り返し可能な場所で切って返却する
   */
  private breakUnits(units: SplitUnit[]): SplitUnit[] {
    for (let index = units.length - 1; index >= 0; --index) {
      if (units[index].isSplitCharacter) {
        return Enumerable.from(units)
          .take(index + 1)
          .toArray();
      }
    }
    // すべて折り返し不可の場合はそのまま表示するしかない
    return units;
  }

  /**
   * iTextSharpとI/Fをあわせている
   */
  private isSplitCharacter(start: number, current: number, end: number, chars: string[]): boolean {
    try {
      if (!this._isWordBreakControlled) { return false; }
      
      const currentChar = chars[current];
      if (this.isUnsafeAtEndOfLine(currentChar)) { return false; }
      if (this.isJoinCharacter(currentChar)) { return false; }

      if (current !== 0) {
          // 前の文字の考慮が必要かどうか
          const prevChar = chars[current - 1];
          if (this.isJoinCharacter(prevChar)) { return false; }
      }

      if (current + 1 < end) {
          // 次の文字の考慮が必要かどうか
          const nextChar = chars[current + 1];
          if (this.isUnsafeAtStartOfLine(nextChar)) { return false; }
          if (this.isJoinCharacter(nextChar)) { return false; }
      }
      return true;
    } catch (e) {
      console.debug(e);
    }
    return false;
  }

  private isJoinCharacter(target: string): boolean {
    if (SplitCharacterMaster.joinChars.indexOf(target) >= 0) { return true; }
    return false;
  }

  private isUnsafeAtEndOfLine(target: string): boolean {
    if (SplitCharacterMaster.startBranchetChars.indexOf(target) >= 0) { return true; }
    if (SplitCharacterMaster.moneySymbolChars.indexOf(target) >= 0) { return true; }
    return false;
}

  private isUnsafeAtStartOfLine(target: string): boolean {
    if (SplitCharacterMaster.endBranchetChars.indexOf(target) >= 0) { return true; }
    if (SplitCharacterMaster.headProhibitedChars.indexOf(target) >= 0) { return true; }
    if (SplitCharacterMaster.separateChars.indexOf(target) >= 0) { return true; }
    if (SplitCharacterMaster.middlePointChars.indexOf(target) >= 0) { return true; }
    if (SplitCharacterMaster.pointChars.indexOf(target) >= 0) { return true; }
    return false;
}
}

class SplitCharacterMaster {
  /**
   * 括弧（終了）類
   */
  static endBranchetChars: string[] = [
    "\u0029", // )
    "\uFF09", // ）
    "\u005D", // ]
    "\uFF3D", // ］
    "\uFF5D", // ｝
    "\u3015", // 〕
    "\u3009", // 〉
    "\u300B", // 》
    "\u300D", // 」
    "\u300F", // 』
    "\u3011", // 】
    "\u3019", // 〙
    "\u3017", // 〗
    "\u301F", // 〟
    "\uFF60", // ｠
  ];

  /**
   * 括弧（開始）類
   */
  static startBranchetChars: string[] = [
    "\u0028", // (
    "\uFF08", // （
    "\uFF3B", // ［
    "\u005B", // [
    "\uFF5B", // ｛
    "\u3014", // 〔
    "\u3008", // 〈
    "\u300A", // 《
    "\u300C", // 「
    "\u300E", // 『
    "\u3010", // 【
    "\u3018", // 〘
    "\u3016", // 〖
    "\u301D", // 〝
    "\uFF5F", // ｟
  ];

  /**
   * 行頭禁則和字
   */
  static headProhibitedChars: string[] = [
    "\u30FD", // ヽ
    "\u30FE", // ヾ
    "\u30FC", // ー
    "\u30A1", // ァ
    "\u30A3", // ィ
    "\u30A5", // ゥ
    "\u30A7", // ェ
    "\u30A9", // ォ
    "\u30C3", // ッ
    "\u30E3", // ャ
    "\u30E5", // ュ
    "\u30E7", // ョ
    "\u30EE", // ヮ
    "\u30F5", // ヵ
    "\u30F6", // ヶ
    "\u3041", // ぁ
    "\u3043", // ぃ
    "\u3045", // ぅ
    "\u3047", // ぇ
    "\u3049", // ぉ
    "\u3063", // っ
    "\u3083", // ゃ
    "\u3085", // ゅ
    "\u3087", // ょ
    "\u308E", // ゎ
    "\u3095", // ゕ
    "\u3096", // ゖ
    "\u31F0", // ㇰ
    "\u31F1", // ㇱ
    "\u31F2", // ㇲ
    "\u31F3", // ㇳ
    "\u31F4", // ㇴ
    "\u31F5", // ㇵ
    "\u31F6", // ㇶ
    "\u31F7", // ㇷ
    "\u31F8", // ㇸ
    "\u31F9", // ㇹ
    "\u31FA", // ㇺ
    "\u31FB", // ㇻ
    "\u31FC", // ㇼ
    "\u31FD", // ㇽ
    "\u31FE", // ㇾ
    "\u31FF", // ㇿ
    "\u3005", // 々
    "\u303B", // 〻
    "\u309D", // ゝ
  ];

  /**
   * 区切り約物
   */
  static separateChars: string[] = [
    "\u002F", // /
    "\u003F", // ?
    "\uFF1F", // ？
    "\u0021", // !
    "\uFF01", // ！
    "\u203C", // ‼
  ];

  /**
   * 中点類
   */
  static middlePointChars: string[] = [
    "\u30FB", // ・
    "\u003A", // :
    "\uFF1A", // ：
    "\u003B", // ;
    "\uFF1B", // ；
  ];

  /**
   * 句点類
   */
  static pointChars: string[] = [
    "\u002C", // ,
    "\uFF0c", // ，
    "\u3001", // 、
    "\u3002", // 。
    "\u002E", // .
    "\uFF0E", // ．
  ];

  /**
   * 分離禁則文字
   */
  static joinChars: string[] = [
    "\u0022", // "
    "\u0027", // '
    "\u2014", // —
    "\u2018", // ‘
    "\u2019", // ’
    "\u201C", // “
    "\u201D", // "
    "\u00AB", // «
    "\u00BB", // »
  ];

  /**
   * 金額記号類
   */
  static moneySymbolChars: string[] = [
    "\u005C", // \
    "\uFFE5", // ￥
    "\u0024", // $
    "\uFF04", // ＄
  ];
}
